import { AuthContext } from '../../context/AuthContext';
import { useLang } from '../../context/LangContext';
import { useEffect, useContext, useState } from 'react';
import { Navigate } from 'react-router-dom';
import esTexts from '../../translations/es.json';
import enTexts from '../../translations/en.json';
import axios from 'axios';
import Welcome from '../../components/Header/Welcome';
import { AdminNav } from '../../components';

const Logos = () => {
  const [selectedLogo, setSelectedLogo] = useState(null);

  const { token, currentUser } = useContext(AuthContext);
  const { lang } = useLang();
  const texts = lang === 'es' ? esTexts : enTexts;

  const [errorSave, setErrorSave] = useState(false);
  const [errorSaveMessage, setErrorSaveMessage] = useState('');
  const [successSave, setSuccessSave] = useState(false);
  const [successSaveMessage, setSuccessSaveMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [logos, setLogos] = useState([]);
  const [web, setWeb] = useState('');

  const getAllLogos = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/${lang}/logos`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('userToken')}`,
        },
      });

      if (response.status === 200) {
        setLogos(response.data.logos);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const deleteLogo = id => async () => {
    const confirm = window.confirm(texts.admin_common.delete_confirm);
    if (!confirm) return;

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/logos/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('userToken')}`,
          },
        }
      );

      if (response.status === 200) {
        getAllLogos();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveLogo = async () => {
    if (!selectedLogo) {
      setErrorSave(true);
      setErrorSaveMessage(
        lang === 'es' ? 'Selecciona una imagen de logo' : 'Please select a logo image'
      );
      return;
    }

    const formData = new FormData();
    formData.append('image', selectedLogo);
    formData.append('web', web);

    try {
      setIsLoading(true);
      setErrorSave(false);
      setSuccessSave(false);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/logos`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        setSuccessSave(true);
        setSuccessSaveMessage(
          lang === 'es' ? 'Logo guardado con éxito' : 'Logo saved successfully'
        );
        setSelectedLogo(null);
        setWeb('');
        const fileInput = document.getElementById('logo');
        if (fileInput) fileInput.value = '';
        getAllLogos();
      }
    } catch (error) {
      setErrorSave(true);
      setErrorSaveMessage(error.response?.data?.message || 'Error al guardar logo');
    } finally {
      setIsLoading(false);
    }
  };

  const moveItem = (index, direction) => {
    const newItems = [...logos];
    const newIndex = index + direction;

    if (newIndex >= 0 && newIndex < newItems.length) {
      const temp = newItems[index];
      newItems[index] = newItems[newIndex];
      newItems[newIndex] = temp;
      setLogos(newItems);
      savePositions(newItems);
    }
  };

  const savePositions = async newItems => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/logos/updatePositionLogo`,
        { positions: newItems },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (token) {
      getAllLogos();
    }
  }, [token]);

  if (!currentUser?.isAdmin) {
    return <Navigate to="/admin" />;
  }

  return (
    <>
      <Welcome />

      <div className="container py-4 mb-5" style={{ minHeight: '80vh' }}>
        <AdminNav />

        <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 pb-2 border-bottom">
          <div>
            <h2 className="fw-bold text-sen mb-1 d-flex align-items-center gap-2">
              <i className="fa fa-picture-o"></i>
              <span>
                {texts.admin_common.list_of} {texts.admin.menu_logos}
              </span>
            </h2>
            <span className="text-muted small">
              {lang === 'es'
                ? 'Gestiona los logos de sponsors y aliados que aparecen en el carrusel superior.'
                : 'Manage sponsor and partner logos displayed on the header banner.'}
            </span>
          </div>
        </div>

        <div className="row g-4">
          {/* Upload Card */}
          <div className="col-12 col-lg-4">
            <div
              className="card border-0 shadow-sm rounded-4 bg-white sticky-top"
              style={{ top: '20px' }}
            >
              <div className="card-body p-4">
                <h5 className="fw-bold text-sen mb-3 d-flex align-items-center gap-2">
                  <i className="fa fa-upload"></i>
                  <span>Nuevo Logo Sponsor</span>
                </h5>

                {errorSave && (
                  <div className="alert alert-danger py-2 small mb-3">{errorSaveMessage}</div>
                )}
                {successSave && (
                  <div className="alert alert-success py-2 small mb-3">{successSaveMessage}</div>
                )}

                <div className="mb-3">
                  <label htmlFor="logo" className="form-label small fw-bold text-secondary">
                    Imagen del Logo (PNG / JPG / WEBP) *
                  </label>
                  <input
                    className="form-control bg-light"
                    type="file"
                    id="logo"
                    accept="image/*"
                    onChange={e => setSelectedLogo(e.target.files[0])}
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="web" className="form-label small fw-bold text-secondary">
                    Sitio Web Asociado (URL)
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0 text-muted">
                      <i className="fa fa-globe"></i>
                    </span>
                    <input
                      className="form-control bg-light border-start-0"
                      type="text"
                      id="web"
                      placeholder="https://ejemplo.com"
                      value={web}
                      onChange={e => setWeb(e.target.value)}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end pt-2 border-top">
                  <button
                    type="button"
                    onClick={handleSaveLogo}
                    disabled={isLoading}
                    className="btn btn-primary bg-sen border-0 px-4 fw-bold shadow-sm"
                  >
                    {isLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-save me-2"></i>
                        {texts.admin_common.save}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Logos Grid */}
          <div className="col-12 col-lg-8">
            <div className="card border-0 shadow-sm rounded-4 bg-white p-4">
              <h5 className="fw-bold text-secondary mb-3">Logos Activos ({logos.length})</h5>

              {logos.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="fa fa-picture-o fs-1 d-block mb-2 text-secondary opacity-50"></i>
                  No hay logos configurados
                </div>
              ) : (
                <div className="row g-3">
                  {logos.map((logo, index) => (
                    <div key={logo.id} className="col-12 col-sm-6 col-md-4">
                      <div className="card border rounded-3 p-3 text-center bg-light h-100 d-flex flex-column justify-content-between shadow-sm">
                        {/* Position controls */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="badge bg-secondary">#{index + 1}</span>
                          <div className="btn-group btn-group-sm">
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => moveItem(index, -1)}
                              disabled={index === 0}
                              title="Mover arriba"
                            >
                              <i className="fa fa-arrow-left"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => moveItem(index, 1)}
                              disabled={index === logos.length - 1}
                              title="Mover abajo"
                            >
                              <i className="fa fa-arrow-right"></i>
                            </button>
                          </div>
                        </div>

                        {/* Image */}
                        <div
                          className="bg-white rounded p-3 mb-2 d-flex align-items-center justify-content-center"
                          style={{ minHeight: '90px' }}
                        >
                          <img
                            src={logo.url}
                            alt="logo"
                            className="img-fluid"
                            style={{ maxHeight: '60px', objectFit: 'contain' }}
                          />
                        </div>

                        {/* Web link */}
                        {logo.web ? (
                          <a
                            href={logo.web}
                            target="_blank"
                            rel="noreferrer"
                            className="small text-truncate text-decoration-none text-primary d-block mb-2"
                            title={logo.web}
                          >
                            <i className="fa fa-external-link me-1"></i>
                            {logo.web.replace(/^https?:\/\//, '')}
                          </a>
                        ) : (
                          <span className="small text-muted mb-2">Sin link</span>
                        )}

                        {/* Delete Button */}
                        <button
                          className="btn btn-outline-danger btn-sm w-100"
                          onClick={deleteLogo(logo.id)}
                        >
                          <i className="fa fa-trash me-1"></i> Eliminar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Logos;
